import React, { Component, ErrorInfo, ReactNode } from "react";
import { toast } from "components/ui/use-toast";
import { Container, Button } from "reactstrap";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  private handleError = (event: ErrorEvent) => {
    // Browsers defer undelivered resize notifications to a later frame.
    // Keep their native console diagnostics without presenting a crash toast.
    // A thrown Error (even with this message) must still be reported below.
    if (event.error == null && (
      event.message === "ResizeObserver loop completed with undelivered notifications." ||
      event.message === "ResizeObserver loop limit exceeded"
    )) {
      return;
    }

    toast({
      title: "Unhandled Application Error",
      description: event.message || "An unexpected error occurred.",
      variant: "rose_red"
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    toast({
      title: "Unhandled Promise Rejection",
      description: event.reason?.message || String(event.reason) || "An unhandled promise rejection occurred.",
      variant: "rose_red"
    });
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    toast({
      title: "Application Render Crash",
      description: error.message || String(error),
      variant: "rose_red"
    });
  }

  public componentDidMount() {
    window.addEventListener("error", this.handleError);
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  public componentWillUnmount() {
    window.removeEventListener("error", this.handleError);
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <Container className="text-center py-5">
          <div className="speem-gray p-4 rounded shadow-sm">
            <h1 className="display-4 text-danger mb-3">
              <i className="fas fa-exclamation-triangle mr-2" />
              Application Error
            </h1>
            <p className="lead">
              Something went wrong inside the application. The error has been captured and routed to the support team.
            </p>
            {this.state.error && (
              <pre className="text-left bg-dark text-white p-3 rounded overflow-auto" style={{ maxHeight: "200px", fontSize: "0.85rem" }}>
                <code>{this.state.error.stack || this.state.error.message}</code>
              </pre>
            )}
            <div className="mt-4">
              <Button color="primary" onClick={() => window.location.reload()}>
                <i className="fas fa-redo mr-1" /> Reload Page
              </Button>
              <Button color="secondary" className="ml-2" onClick={() => window.location.href = "/"}>
                <i className="fas fa-home mr-1" /> Go to Home
              </Button>
            </div>
          </div>
        </Container>
      );
    }

    return this.props.children;
  }
}
export default ErrorBoundary;
